var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Extension } from '@tiptap/core';
import CharacterCount from '@tiptap/extension-character-count';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import classNames from 'classnames';
import { useFeatureFlags } from 'frontend/js/modules/infra/feature-flags';
import { useClickOutside } from 'frontend/js/modules/infra/small-tools/use-click-outside';
import { Plugin } from 'prosemirror-state';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AiButton } from '../../../../../app/engagement/components/AiButton';
import { TextM, TextS } from '../../../../infra/dls/components/typography/typography';
import { useBreakpoints } from '../../../../infra/dls/hooks/use-get-breakpoints';
import { Dropdown, DropdownContent } from '../../../../infra/ui/dropdown';
import { withAliases } from '../../../../infra/ui/utils/with-aliases';
import { AIPopoverContainer as AIPopover } from '../../ai-popover/ai-popover.container';
import { ActionBar } from '../action-bar';
import { BubbleMenu } from './components/bubble-menu';
import { SimpleEditor } from './components/simple-editor';
import { Limit } from './rich-text-editor-limit';
import styles from './styles.module.scss';
import { RichTextEditorEvents } from './types';
import { removeUnicode } from './utils/remove-unicode';
import { EngageTracker } from '../../tracking/adapter';
import { ENGAGE_GEN_AI_POPOVER_CLOSED, ENGAGE_GEN_AI_POPOVER_OPENED, ENGAGE_GEN_AI_PROMPT_SUBMITTED, ENGAGE_GEN_AI_TEXT_REQUEST_FAILED, ENGAGE_GEN_AI_TEXT_RESULT_APPLIED, ENGAGE_GEN_AI_UNDO_CLICKED, } from '../../tracking/events';
var CustomLink = Link.extend({
    inclusive: false,
});
var dispatchUpdateCountEvent = function (id, count, limit) {
    if (!limit)
        return;
    var event = new CustomEvent(RichTextEditorEvents.UPDATE_COUNT, {
        detail: {
            id: id,
            count: count,
            limit: limit,
        },
    });
    document.dispatchEvent(event);
};
// Mobile AI Popover Component
var MobileAiPopover = function (_a) {
    var isOpen = _a.isOpen, onClose = _a.onClose, onRephraseClick = _a.onRephraseClick, onMoreFormalClick = _a.onMoreFormalClick, onMorePlayfulClick = _a.onMorePlayfulClick, onShortenClick = _a.onShortenClick, onElaborateClick = _a.onElaborateClick, onPromptSubmit = _a.onPromptSubmit, isLoading = _a.isLoading, isFetchingSuggestions = _a.isFetchingSuggestions, currentAction = _a.currentAction, autoFocusPrompt = _a.autoFocusPrompt, dynamicSuggestions = _a.dynamicSuggestions, suggestionsAreDirectContent = _a.suggestionsAreDirectContent, onInsertContent = _a.onInsertContent, onUndoClick = _a.onUndoClick, hasUndoContent = _a.hasUndoContent, isEmpty = _a.isEmpty, _b = _a.isShortenAllowed, isShortenAllowed = _b === void 0 ? false : _b, section = _a.section;
    var popoverRef = useRef(null);
    var _c = useState(0), keyboardHeight = _c[0], setKeyboardHeight = _c[1];
    useClickOutside(popoverRef, onClose);
    // Detect keyboard appearance on mobile
    useEffect(function () {
        if (!isOpen)
            return;
        var cleanup;
        // Use Visual Viewport API if available (better performance)
        if (window.visualViewport) {
            var handleViewportChange_1 = function () {
                var viewportHeight = window.visualViewport.height;
                var windowHeight = window.innerHeight;
                var detectedKeyboardHeight = Math.max(0, windowHeight - viewportHeight);
                // if (detectedKeyboardHeight <= 0) {
                //   onClose();
                //   return;
                // }
                setKeyboardHeight(detectedKeyboardHeight);
            };
            // Listen to both resize and scroll events for better responsiveness
            window.visualViewport.addEventListener('resize', handleViewportChange_1);
            window.visualViewport.addEventListener('scroll', handleViewportChange_1);
            handleViewportChange_1(); // Initial check
            cleanup = function () {
                var _a, _b;
                (_a = window.visualViewport) === null || _a === void 0 ? void 0 : _a.removeEventListener('resize', handleViewportChange_1);
                (_b = window.visualViewport) === null || _b === void 0 ? void 0 : _b.removeEventListener('scroll', handleViewportChange_1);
            };
        }
        else {
            // Fallback: detect viewport height changes with debouncing
            var initialHeight_1 = window.innerHeight;
            var timeoutId_1;
            var handleResize_1 = function () {
                // Clear previous timeout to debounce
                clearTimeout(timeoutId_1);
                timeoutId_1 = setTimeout(function () {
                    var currentHeight = window.innerHeight;
                    var heightDiff = initialHeight_1 - currentHeight;
                    // If height decreased significantly, assume keyboard is open
                    // Use a smaller threshold for better detection
                    if (heightDiff > 100) {
                        setKeyboardHeight(heightDiff);
                    }
                    else {
                        setKeyboardHeight(0);
                    }
                }, 50); // 50ms debounce for better performance
            };
            window.addEventListener('resize', handleResize_1);
            cleanup = function () {
                window.removeEventListener('resize', handleResize_1);
                clearTimeout(timeoutId_1);
            };
        }
        // eslint-disable-next-line consistent-return
        return function () {
            cleanup === null || cleanup === void 0 ? void 0 : cleanup();
            setKeyboardHeight(0);
        };
    }, [isOpen, onClose]);
    if (!isOpen)
        return null;
    // Calculate position to appear above keyboard with safe area
    var containerStyle = keyboardHeight > 0
        ? {
            bottom: "".concat(keyboardHeight, "px"),
            position: 'fixed',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1003,
        }
        : {}; // Use default CSS positioning when no keyboard
    return createPortal(_jsx("div", { className: styles['mobile-ai-popover-overlay'], children: _jsx("div", { ref: popoverRef, className: styles['mobile-ai-popover-container'], style: containerStyle, children: _jsx(AIPopover, { onClose: onClose, onRephraseClick: onRephraseClick, onMoreFormalClick: onMoreFormalClick, onMorePlayfulClick: onMorePlayfulClick, onShortenClick: onShortenClick, onElaborateClick: onElaborateClick, onPromptSubmit: onPromptSubmit, isLoading: isLoading, isFetchingSuggestions: isFetchingSuggestions, currentAction: currentAction, autoFocusPrompt: autoFocusPrompt, dynamicSuggestions: dynamicSuggestions, suggestionsAreDirectContent: suggestionsAreDirectContent, onInsertContent: onInsertContent, onUndoClick: onUndoClick, hasUndoContent: hasUndoContent, isEmpty: isEmpty, isShortenAllowed: isShortenAllowed, section: section }) }) }), document.body);
};
var BaseRichTextEditor = forwardRef(function (_a, ref) {
    var _b;
    var id = _a.id, onChange = _a.onChange, _c = _a.variant, variant = _c === void 0 ? 'simple' : _c, _d = _a.allowEnters, allowEnters = _d === void 0 ? false : _d, _e = _a.displayAsMultiline, displayAsMultiline = _e === void 0 ? false : _e, _f = _a.disabled, disabled = _f === void 0 ? false : _f, _g = _a.placeholder, placeholder = _g === void 0 ? 'Start typing...' : _g, content = _a.content, errorMessage = _a.errorMessage, editorCustomStyles = _a.editorCustomStyles, editorWrapperCustomStyles = _a.editorWrapperCustomStyles, limit = _a.limit, _h = _a.hideLimit, hideLimit = _h === void 0 ? false : _h, _j = _a.mode, mode = _j === void 0 ? 'darker' : _j, automationId = _a.automationId, _k = _a.stripUnicode, stripUnicode = _k === void 0 ? false : _k, 
    // AI functionality props
    _l = _a.aiSuggestions, 
    // AI functionality props
    aiSuggestions = _l === void 0 ? [] : _l, onFetchAiSuggestions = _a.onFetchAiSuggestions, onRefineText = _a.onRefineText, _m = _a.isRefining, isRefining = _m === void 0 ? false : _m, _o = _a.isFetchingSuggestions, isFetchingSuggestions = _o === void 0 ? false : _o, _p = _a.suggestionsAreDirectContent, suggestionsAreDirectContent = _p === void 0 ? false : _p, _q = _a.isShowAiGenerateButton, isShowAiGenerateButton = _q === void 0 ? false : _q, _r = _a.aiPopoverTop, aiPopoverTop = _r === void 0 ? false : _r, _s = _a.isShortenAllowed, isShortenAllowed = _s === void 0 ? false : _s, 
    // AI popover coordination props
    onAiPopoverOpen = _a.onAiPopoverOpen, forceCloseAiPopover = _a.forceCloseAiPopover, trackingSection = _a.trackingSection;
    var _t = useState(false), isFocused = _t[0], setIsFocused = _t[1];
    var _u = useState(false), isActionBarFocused = _u[0], setIsActionBarFocused = _u[1];
    var _v = useState(false), isAiPopoverOpen = _v[0], setIsAiPopoverOpen = _v[1];
    var _w = useState(false), isAiButtonVisible = _w[0], setIsAiButtonVisible = _w[1];
    var _x = useState(null), highlightedRange = _x[0], setHighlightedRange = _x[1];
    var _y = useState([]), undoStack = _y[0], setUndoStack = _y[1];
    var _z = useState(0), count = _z[0], setCount = _z[1];
    var editorContainerRef = useRef(null);
    var isMobileOrSmaller = useBreakpoints().isMobileOrSmaller;
    var engageGenAiEnabled = useFeatureFlags().engageGenAiEnabled;
    var _0 = useState(''), currentAiAction = _0[0], setCurrentAiAction = _0[1];
    var editor = useEditor({
        extensions: [
            StarterKit.configure({
                paragraph: {
                    HTMLAttributes: {
                        class: !allowEnters && !displayAsMultiline ? styles['single-line'] : '',
                    },
                },
                hardBreak: !allowEnters ? false : undefined,
            }),
            CharacterCount.configure({
                limit: limit,
            }),
            Underline,
            Highlight.configure({
                multicolor: true,
            }),
            TextAlign.configure({
                types: ['heading', 'paragraph'],
                defaultAlignment: 'center',
                alignments: ['left', 'center'],
            }),
            Placeholder.configure({
                placeholder: placeholder,
                showOnlyWhenEditable: false,
            }),
            CustomLink.configure({
                openOnClick: false,
                linkOnPaste: false,
                HTMLAttributes: {
                    target: '_blank',
                    rel: 'noopener noreferrer',
                    class: styles.link,
                },
                defaultProtocol: 'https',
            }),
            Extension.create({
                addKeyboardShortcuts: function () {
                    return {
                        Enter: function () { return !allowEnters; },
                    };
                },
                addProseMirrorPlugins: function () {
                    return [
                        new Plugin({
                            props: {
                                handleTextInput: function (view, from, to, text) {
                                    if (!stripUnicode)
                                        return false;
                                    var strippedText = removeUnicode(text);
                                    if (strippedText === text) {
                                        return false;
                                    }
                                    if (strippedText.length === 0) {
                                        return true;
                                    }
                                    view.dispatch(view.state.tr.replaceWith(from, to, view.state.schema.text(strippedText)));
                                    return true;
                                },
                                handlePaste: function (view, event) {
                                    var _a;
                                    event.preventDefault();
                                    // we always read only the plain text from the clipboard to prevent formatting
                                    var contentToPaste = ((_a = event.clipboardData) === null || _a === void 0 ? void 0 : _a.getData('text/plain')) || '';
                                    if (stripUnicode) {
                                        contentToPaste = removeUnicode(contentToPaste);
                                    }
                                    // A very specific fix, if allowEnters is false
                                    // if allowEnters is false, this is a workaround to prevent the default behavior of pasting newlines
                                    if (!allowEnters) {
                                        contentToPaste = contentToPaste.replace(/[\n\r]/g, ' ');
                                    }
                                    view.dispatch(view.state.tr.insertText(contentToPaste));
                                    return true;
                                },
                            },
                        }),
                    ];
                },
            }),
        ],
        content: content,
        editable: !disabled,
        onFocus: function () { return setIsFocused(true); },
        onBlur: function () { return setIsFocused(false); },
        onUpdate: function (_a) {
            var editorInstance = _a.editor;
            onChange === null || onChange === void 0 ? void 0 : onChange(editorInstance.getHTML());
            var currentCount = variant === 'simple' ? count : editorInstance.storage.characterCount.characters() || 0;
            dispatchUpdateCountEvent(id, currentCount, limit);
        },
    });
    useEffect(function () {
        if (variant === 'simple') {
            var currentCount = variant === 'simple' ? count : (editor === null || editor === void 0 ? void 0 : editor.storage.characterCount.characters()) || 0;
            dispatchUpdateCountEvent(id, currentCount, limit);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [count, limit]);
    // Handle force close from parent coordination
    useEffect(function () {
        if (forceCloseAiPopover && isAiPopoverOpen) {
            setIsAiPopoverOpen(false);
            setIsAiButtonVisible(false);
            setHighlightedRange(null);
            setUndoStack([]);
            // Remove highlight when popover closes
            if (editor) {
                var _a = editor.state.selection, from = _a.from, to = _a.to;
                editor.chain().focus().selectAll().unsetHighlight().setTextSelection({ from: from, to: to }).run();
            }
        }
    }, [forceCloseAiPopover, isAiPopoverOpen, editor]);
    // Helper function to check if content is empty (consistent with container logic)
    var isContentEmpty = function () {
        var currentContent = variant === 'simple' ? content : (editor === null || editor === void 0 ? void 0 : editor.getText()) || '';
        if (!currentContent)
            return true;
        var trimmed = currentContent === null || currentContent === void 0 ? void 0 : currentContent.trim();
        return trimmed === '' || trimmed === '<p></p>' || /^<p[^>]*><\/p>$/.test(trimmed);
    };
    // Monitor content changes and fetch suggestions if content becomes empty while popover is open
    useEffect(function () {
        if (isAiPopoverOpen && onFetchAiSuggestions) {
            // If content is now empty and we don't have suggestions, fetch them
            var isEmpty = isContentEmpty();
            if (isEmpty && aiSuggestions.length === 0) {
                // Small delay to prevent rapid re-fetching during typing
                var timeoutId_2 = setTimeout(function () {
                    onFetchAiSuggestions();
                }, 100);
                return function () { return clearTimeout(timeoutId_2); };
            }
        }
        // Return undefined when no cleanup is needed
        return undefined;
    }, [content, isAiPopoverOpen, aiSuggestions.length]); // Don't include onFetchAiSuggestions to prevent loops
    // Expose methods to get limit related data
    useImperativeHandle(ref, function () { return ({
        getCharacterCount: function () {
            return variant === 'simple' ? count : (editor === null || editor === void 0 ? void 0 : editor.storage.characterCount.characters()) || 0;
        },
        getCharacterLimit: function () { return limit; },
        getRemainingCharacters: function () {
            if (!limit)
                return undefined;
            var currentCount = variant === 'simple' ? count : (editor === null || editor === void 0 ? void 0 : editor.storage.characterCount.characters()) || 0;
            return limit - currentCount;
        },
        getEditorId: function () { return id; },
    }); });
    // AI action handlers
    var handleAiAction = function (promptText, actionLabel) {
        var _a;
        if (!editor || !onRefineText) {
            console.warn('Editor or refine function not available for AI processing');
            return;
        }
        // Check if there's a text selection (indicating bubble menu trigger) or highlighted content
        var hasSelection = !editor.state.selection.empty;
        var hasHighlightedContent = highlightedRange && !hasSelection;
        // For API: original should always be the whole text, current should be selected or highlighted text
        var wholeText = (_a = (variant === 'simple' ? content : editor.getText() || '')) === null || _a === void 0 ? void 0 : _a.trim();
        var selectedText = '';
        if (hasSelection) {
            selectedText = editor.state.doc.textBetween(editor.state.selection.from, editor.state.selection.to);
        }
        else if (hasHighlightedContent) {
            selectedText = editor.state.doc.textBetween(highlightedRange.from, highlightedRange.to);
        }
        // Store the current text content in undo stack before making changes
        var currentTextContent = hasSelection || hasHighlightedContent ? selectedText : wholeText;
        if (currentTextContent === null || currentTextContent === void 0 ? void 0 : currentTextContent.trim()) {
            setUndoStack(function (prev) { return __spreadArray(__spreadArray([], prev, true), [currentTextContent], false); });
        }
        setCurrentAiAction(actionLabel);
        // For selection-based refinement: original = whole text, partial = selected text
        // For full content refinement: original = whole text, partial = undefined
        var partialText = hasSelection || hasHighlightedContent ? selectedText : undefined;
        EngageTracker.getInstance().track(ENGAGE_GEN_AI_PROMPT_SUBMITTED, {
            section: trackingSection,
            promptType: actionLabel === promptText ? 'custom' : 'preset',
            originalLength: (wholeText === null || wholeText === void 0 ? void 0 : wholeText.length) || 0,
            partialLength: (partialText === null || partialText === void 0 ? void 0 : partialText.length) || 0,
            hadSelection: !!partialText,
        });
        onRefineText(promptText, wholeText, partialText)
            .then(function (response) {
            var _a;
            // Update the editor content with the AI-processed text
            if (editor && response.text) {
                if (hasSelection || hasHighlightedContent) {
                    // Use selection range if available, otherwise use highlighted range
                    var _b = hasSelection ? editor.state.selection : highlightedRange, from = _b.from, to = _b.to;
                    // Replace the text and reapply highlighting to new content
                    editor.chain().focus().setTextSelection({ from: from, to: to }).unsetHighlight().insertContent(response.text).run();
                    // Highlight the newly inserted content without selecting it
                    var newTo = from + response.text.length;
                    editor
                        .chain()
                        .setTextSelection({ from: from, to: newTo })
                        .setHighlight({ color: '#ADD8E6' })
                        .setTextSelection(newTo) // Move cursor to end, no selection
                        .run();
                    // Store the highlighted range for future AI operations
                    setHighlightedRange({ from: from, to: newTo });
                }
                else {
                    // Replace entire content
                    editor.commands.setContent(response.text);
                    onChange === null || onChange === void 0 ? void 0 : onChange(response.text);
                }
            }
            EngageTracker.getInstance().track(ENGAGE_GEN_AI_TEXT_RESULT_APPLIED, {
                section: trackingSection,
                applyType: partialText ? 'replace_selection' : 'replace_all',
                responseLength: ((_a = response === null || response === void 0 ? void 0 : response.text) === null || _a === void 0 ? void 0 : _a.length) || 0,
            });
            // Don't close the popover automatically - let user close it manually
            setCurrentAiAction('');
        })
            .catch(function (error) {
            console.error('AI text processing failed:', error);
            EngageTracker.getInstance().track(ENGAGE_GEN_AI_TEXT_REQUEST_FAILED, {
                section: trackingSection,
            });
            setCurrentAiAction('');
            // Don't close popover on error either
        });
    };
    // Handle undo action
    var handleUndoClick = function () {
        if (editor && undoStack.length > 1) {
            EngageTracker.getInstance().track(ENGAGE_GEN_AI_UNDO_CLICKED, { section: trackingSection });
            // Remove the last state and get the previous one
            var newStack = __spreadArray([], undoStack, true);
            newStack.pop(); // Remove current state
            var previousContent = newStack[newStack.length - 1]; // Get previous state
            setUndoStack(newStack);
            // Check if there's a selection or highlighted content
            var hasSelection = !editor.state.selection.empty;
            var hasHighlightedContent = highlightedRange && !hasSelection;
            if (hasSelection || hasHighlightedContent) {
                // Restore selected or highlighted text only
                var _a = hasSelection ? editor.state.selection : highlightedRange, from = _a.from, to = _a.to;
                editor.chain().focus().setTextSelection({ from: from, to: to }).unsetHighlight().insertContent(previousContent).run();
                // Update the highlighted range for the restored content
                var newTo = from + previousContent.length;
                editor
                    .chain()
                    .setTextSelection({ from: from, to: newTo })
                    .setHighlight({ color: '#ADD8E6' })
                    .setTextSelection(newTo) // Move cursor to end, no selection
                    .run();
                // Update highlighted range
                setHighlightedRange({ from: from, to: newTo });
            }
            else {
                // Restore entire content
                editor.commands.setContent(previousContent);
                onChange === null || onChange === void 0 ? void 0 : onChange(editor.getText());
            }
            // Don't close the popover automatically - let user close it manually
        }
    };
    var handleRephraseClick = function () {
        return handleAiAction('Rephrase this text to make it clearer and more engaging', 'Rephrase');
    };
    var handleMoreFormalClick = function () { return handleAiAction('Make this text more formal and professional', 'More formal'); };
    var handleMorePlayfulClick = function () {
        return handleAiAction('Make this text more playful, fun, and engaging', 'More playful');
    };
    var handleShortenClick = function () {
        return handleAiAction('Make this text shorter and more concise while keeping the main message', 'Shorten');
    };
    var handleElaborateClick = function () {
        return handleAiAction('Expand on this text with more details and explanation', 'Elaborate');
    };
    var handlePromptSubmit = function (customPrompt) { return handleAiAction(customPrompt, customPrompt); };
    // Handle direct content insertion for backend suggestions
    var handleInsertContent = function (textContent) {
        if (editor) {
            // Store current text content in undo stack before inserting new content
            var currentTextContent_1 = editor.getText();
            if ((currentTextContent_1 === null || currentTextContent_1 === void 0 ? void 0 : currentTextContent_1.trim()) !== '') {
                setUndoStack(function (prev) { return __spreadArray(__spreadArray([], prev, true), [currentTextContent_1], false); });
            }
            editor.commands.setContent(textContent);
            onChange === null || onChange === void 0 ? void 0 : onChange(textContent);
            // Don't close the popover automatically - let user close it manually
        }
    };
    if (!editor) {
        return null;
    }
    var handleBlur = function () {
        setIsFocused(false);
        setTimeout(function () {
            setIsAiButtonVisible(false);
        }, 200);
    };
    var handleFocus = function () {
        setIsFocused(true);
        setIsAiButtonVisible(true);
    };
    var handleBubbleMenuFocus = function () {
        // Only show the regular AI button if there's no selection
        // When there's a selection, the bubble menu AI button is shown instead
        if (editor === null || editor === void 0 ? void 0 : editor.state.selection.empty) {
            setIsAiButtonVisible(true);
        }
        setIsActionBarFocused(true);
    };
    var handleAiPopUpClose = function () {
        setIsAiPopoverOpen(false);
        setIsAiButtonVisible(false);
        setHighlightedRange(null);
        setUndoStack([]); // Clear undo stack when closing popover
        // Remove highlight when popover closes
        if (editor) {
            // Clear any existing highlights in the document using toggleHighlight
            // This will remove any highlights without affecting the selection
            var _a = editor.state.selection, from = _a.from, to = _a.to;
            editor.chain().focus().selectAll().unsetHighlight().setTextSelection({ from: from, to: to }).run();
        }
        EngageTracker.getInstance().track(ENGAGE_GEN_AI_POPOVER_CLOSED, { section: trackingSection });
    };
    // Handle AI popover trigger from bubble menu
    var handleBubbleMenuAiPopover = function () {
        var _a;
        if (!editor.state.selection.empty) {
            // Notify parent that this AI popover is opening
            onAiPopoverOpen === null || onAiPopoverOpen === void 0 ? void 0 : onAiPopoverOpen();
            EngageTracker.getInstance().track(ENGAGE_GEN_AI_POPOVER_OPENED, {
                section: trackingSection,
                source: 'bubble_menu',
                contentLength: ((_a = (variant === 'simple' ? content : (editor === null || editor === void 0 ? void 0 : editor.getText()) || '')) === null || _a === void 0 ? void 0 : _a.length) || 0,
                hasSelection: true,
                suggestionsDirectContent: !!suggestionsAreDirectContent,
            });
            // Store the selection range for future AI operations
            var _b = editor.state.selection, from = _b.from, to = _b.to;
            setHighlightedRange({ from: from, to: to });
            // Initialize undo stack with the original selected text
            var originalText = editor.state.doc.textBetween(from, to);
            setUndoStack([originalText]);
            // Apply highlight to the selected text using TipTap's highlight extension
            editor.chain().focus().setHighlight({ color: '#ADD8E6' }).run();
            onFetchAiSuggestions === null || onFetchAiSuggestions === void 0 ? void 0 : onFetchAiSuggestions();
            setIsAiPopoverOpen(true);
            setIsAiButtonVisible(false);
            setIsActionBarFocused(false);
            editor.commands.blur();
        }
        else {
            console.warn('No text selected, cannot open AI popover from bubble menu');
            setIsAiButtonVisible(true);
        }
    };
    var handleContentLengthChange = function (length) {
        setCount(length);
    };
    var handleAiButtonClick = function () {
        var _a;
        // Notify parent that this AI popover is opening
        onAiPopoverOpen === null || onAiPopoverOpen === void 0 ? void 0 : onAiPopoverOpen();
        EngageTracker.getInstance().track(ENGAGE_GEN_AI_POPOVER_OPENED, {
            section: trackingSection,
            source: 'action_bar',
            contentLength: ((_a = (variant === 'simple' ? content : (editor === null || editor === void 0 ? void 0 : editor.getText()) || '')) === null || _a === void 0 ? void 0 : _a.length) || 0,
            hasSelection: false,
            suggestionsDirectContent: !!suggestionsAreDirectContent,
        });
        // make a hidden input focused
        var hiddenInput = document.createElement('input');
        hiddenInput.type = 'text';
        hiddenInput.style.position = 'absolute';
        hiddenInput.style.left = '-9999px';
        hiddenInput.style.top = '-9999px';
        document.body.appendChild(hiddenInput);
        hiddenInput.focus();
        // Initialize undo stack with the current content
        var currentContent = variant === 'simple' ? content : (editor === null || editor === void 0 ? void 0 : editor.getText()) || '';
        setUndoStack([currentContent]);
        // Fetch suggestions when opening AI popover
        onFetchAiSuggestions === null || onFetchAiSuggestions === void 0 ? void 0 : onFetchAiSuggestions();
        setIsAiPopoverOpen(true);
        setIsAiButtonVisible(false);
        setTimeout(function () {
            hiddenInput.remove();
        }, 100);
    };
    var editorClasses = classNames(styles['text-editor'], (_b = {},
        _b[styles['is-disabled']] = disabled,
        _b[styles['is-active']] = editor === null || editor === void 0 ? void 0 : editor.isActive('link'),
        _b[styles['is-focused']] = isFocused || isActionBarFocused,
        _b[styles['has-error']] = !!errorMessage,
        _b[styles['ai-border']] = isAiPopoverOpen,
        _b[styles.readonly] = variant === 'readonly',
        _b[styles[mode]] = mode,
        _b));
    var limitCount = variant === 'simple' ? count : editor.storage.characterCount.characters();
    // AI generate button should be visible when:
    // 1. Feature is enabled and button visibility is true
    // 2. AI popover is not open
    // 3. No text is selected (otherwise bubble menu AI button is shown)
    var isAiGenerateButtonVisible = engageGenAiEnabled && isAiButtonVisible && !isAiPopoverOpen && (editor === null || editor === void 0 ? void 0 : editor.state.selection.empty);
    var renderEditor = function () {
        if (variant === 'readonly') {
            // eslint-disable-next-line react/no-danger
            return _jsx("div", { style: editorCustomStyles, dangerouslySetInnerHTML: { __html: content } });
        }
        if (variant === 'simple') {
            return (_jsx(SimpleEditor, { editorCustomStyles: editorCustomStyles, onChange: onChange, placeholder: placeholder, disabled: disabled, onBlur: handleBlur, onFocus: handleFocus, onContentLengthChange: handleContentLengthChange, limit: limit, allowEnters: allowEnters, displayAsMultiline: displayAsMultiline, content: content, automationId: automationId, stripUnicode: stripUnicode }));
        }
        return (_jsxs(_Fragment, { children: [_jsx(BubbleMenu, { editor: editor, onFocus: handleBubbleMenuFocus, onBlur: function () { return setTimeout(function () { return setIsActionBarFocused(false); }, 300); }, focused: (editor === null || editor === void 0 ? void 0 : editor.isFocused) || isActionBarFocused, onOpenAiPopover: handleBubbleMenuAiPopover, genAiEnabled: engageGenAiEnabled }), _jsx(EditorContent, { editor: editor, className: styles['editor-content'], "data-automation-id": automationId, onFocus: handleFocus, onBlur: handleBlur })] }));
    };
    var getAIPopoverDesktop = function () {
        return (_jsx(Dropdown, { isOpen: isAiPopoverOpen, controlled: true, className: styles['ai-popover-dropdown-container'], children: _jsx(DropdownContent, { side: aiPopoverTop ? 'top' : 'bottom', align: "center", className: styles["ai-popover-dropdown-container--".concat(aiPopoverTop ? 'top' : 'bottom')], children: _jsx(AIPopover, { onClose: handleAiPopUpClose, onRephraseClick: handleRephraseClick, onMoreFormalClick: handleMoreFormalClick, onMorePlayfulClick: handleMorePlayfulClick, onShortenClick: handleShortenClick, onElaborateClick: handleElaborateClick, onPromptSubmit: handlePromptSubmit, isLoading: isRefining, isFetchingSuggestions: isFetchingSuggestions, currentAction: currentAiAction, dynamicSuggestions: aiSuggestions, suggestionsAreDirectContent: suggestionsAreDirectContent, onInsertContent: handleInsertContent, onUndoClick: handleUndoClick, hasUndoContent: undoStack.length > 1, autoFocusPrompt: true, isEmpty: isContentEmpty(), isShortenAllowed: isShortenAllowed, section: trackingSection }) }) }));
    };
    return (_jsxs("div", { children: [_jsx("div", { className: styles['text-editor-container'], ref: editorContainerRef, children: _jsxs("div", { className: editorClasses, style: editorWrapperCustomStyles, children: [!isMobileOrSmaller && aiPopoverTop && getAIPopoverDesktop(), isShowAiGenerateButton && (_jsx(ActionBar, { visible: isAiGenerateButtonVisible, onClose: function () { }, alignment: "start", relativeToParent: true, onBlur: function () { return setIsAiButtonVisible(false); }, children: _jsx(ActionBar.Row, { children: _jsx(ActionBar.RowSection, { children: _jsx(AiButton, { text: (function () {
                                            var _a;
                                            var hasContent = variant === 'simple' ? content === null || content === void 0 ? void 0 : content.trim() : (_a = editor === null || editor === void 0 ? void 0 : editor.getText()) === null || _a === void 0 ? void 0 : _a.trim();
                                            return hasContent ? 'Refine text' : 'Generate text';
                                        })(), onClick: handleAiButtonClick }) }) }) })), renderEditor()] }) }), isMobileOrSmaller && (_jsx(MobileAiPopover, { isOpen: isAiPopoverOpen, onClose: handleAiPopUpClose, onRephraseClick: handleRephraseClick, onMoreFormalClick: handleMoreFormalClick, onMorePlayfulClick: handleMorePlayfulClick, onShortenClick: handleShortenClick, onElaborateClick: handleElaborateClick, onPromptSubmit: handlePromptSubmit, isLoading: isRefining, isFetchingSuggestions: isFetchingSuggestions, currentAction: currentAiAction, autoFocusPrompt: true, dynamicSuggestions: aiSuggestions, suggestionsAreDirectContent: suggestionsAreDirectContent, onInsertContent: handleInsertContent, onUndoClick: handleUndoClick, hasUndoContent: undoStack.length > 1, isEmpty: isContentEmpty(), isShortenAllowed: isShortenAllowed, section: trackingSection })), !isMobileOrSmaller && !aiPopoverTop && getAIPopoverDesktop(), !hideLimit && limit > 0 && !errorMessage && (_jsxs(TextM, { color: "tertiary", className: styles['character-count'], children: [limit - limitCount, "/", limit, " characters left"] })), errorMessage && (_jsx(TextS, { color: "negative", className: styles.error, children: errorMessage }))] }));
});
BaseRichTextEditor.displayName = 'RichTextEditor';
export var RichTextEditor = withAliases(BaseRichTextEditor, {
    Limit: Limit,
});
